import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import {
  ChatMensaje,
  ChatMensajeBackend,
  EscribiendoPayload,
  MensajeEliminadoPayload,
  MensajeLeidoPayload,
  PresenciaPayload,
} from '../models/chat.model';
import { parseMensaje } from './chat-api.service';

export type HubStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/**
 * Cliente SignalR del chat. Se conecta a `/hubs/chat` autenticado con el JWT
 * del `AuthService` vía query string `?access_token=` (estándar SignalR + JWT).
 *
 * Expone Subjects por evento que la página del chat consume con `takeUntilDestroyed`.
 *
 * Auto-reconnect: 0s → 2s → 5s → 10s → 30s (lo mismo que el doc recomienda).
 * Si el cliente queda sin conexión, los mensajes que el usuario mande deben
 * encolarse del lado de la página (no del servicio) — esto es UX no transporte.
 */
@Injectable({ providedIn: 'root' })
export class ChatHubService {
  private auth = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  private hub: HubConnection | null = null;
  private readonly _status = new BehaviorSubject<HubStatus>('disconnected');

  // Subjects de eventos broadcast-eados por el server.
  readonly mensajeRecibido$ = new Subject<ChatMensaje>();
  readonly mensajeLeido$ = new Subject<MensajeLeidoPayload>();
  readonly mensajeEditado$ = new Subject<ChatMensaje>();
  readonly mensajeEliminado$ = new Subject<MensajeEliminadoPayload>();
  readonly usuarioConectado$ = new Subject<PresenciaPayload>();
  readonly usuarioDesconectado$ = new Subject<PresenciaPayload>();
  readonly usuarioEscribiendo$ = new Subject<EscribiendoPayload>();
  readonly usuarioDejoDeEscribir$ = new Subject<EscribiendoPayload>();

  readonly status$ = this._status.asObservable();
  get status(): HubStatus { return this._status.value; }

  /**
   * Inicializa la conexión al hub. Idempotente: si ya está conectado no hace nada.
   * Se llama desde el `OnInit` de la página del chat (o del `ChatNotificationsService`
   * que necesita escuchar globalmente).
   */
  async start(): Promise<void> {
    if (this.hub && this.hub.state !== HubConnectionState.Disconnected) return;

    this._status.next('connecting');
    this.hub = new HubConnectionBuilder()
      .withUrl('/hubs/chat', {
        accessTokenFactory: () => this.auth.accessToken ?? '',
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Information)
      .build();

    this.bindHandlers(this.hub);

    this.hub.onreconnecting(() => this._status.next('reconnecting'));
    this.hub.onreconnected(() => this._status.next('connected'));
    this.hub.onclose(() => this._status.next('disconnected'));

    try {
      await this.hub.start();
      this._status.next('connected');
    } catch (err) {
      this._status.next('disconnected');
      console.error('[ChatHub] no se pudo conectar:', err);
      throw err;
    }
  }

  async stop(): Promise<void> {
    if (this.hub && this.hub.state !== HubConnectionState.Disconnected) {
      await this.hub.stop();
    }
    this._status.next('disconnected');
  }

  // ===== Métodos invocables (cliente → servidor) =====

  /**
   * Envía un mensaje al `destinatarioId`. Retorna el `ChatMensaje` persistido
   * (con `Id` y `EnviadoEn` server-side). El server también lo broadcast-ea a
   * los dos participantes vía `MensajeRecibido` → el listener actualiza la UI
   * (incluido el caller, así no hay que insertarlo manualmente).
   */
  async enviarMensaje(destinatarioId: number, texto: string): Promise<ChatMensaje> {
    this.ensureConnected();
    const m = await this.hub!.invoke<ChatMensajeBackend>('EnviarMensaje', destinatarioId, texto);
    return parseMensaje(m);
  }

  escribiendo(destinatarioId: number): Promise<void> {
    if (!this.hub || this.hub.state !== HubConnectionState.Connected) return Promise.resolve();
    return this.hub.invoke('Escribiendo', destinatarioId);
  }

  dejoDeEscribir(destinatarioId: number): Promise<void> {
    if (!this.hub || this.hub.state !== HubConnectionState.Connected) return Promise.resolve();
    return this.hub.invoke('DejoDeEscribir', destinatarioId);
  }

  // ===== Internals =====

  private bindHandlers(hub: HubConnection): void {
    hub.on('MensajeRecibido', (m: ChatMensajeBackend) =>
      this.mensajeRecibido$.next(parseMensaje(m)));
    hub.on('MensajeEditado', (m: ChatMensajeBackend) =>
      this.mensajeEditado$.next(parseMensaje(m)));
    hub.on('MensajeLeido', (p: MensajeLeidoPayload) => this.mensajeLeido$.next(p));
    hub.on('MensajeEliminado', (p: MensajeEliminadoPayload) => this.mensajeEliminado$.next(p));
    hub.on('UsuarioConectado', (p: PresenciaPayload) => this.usuarioConectado$.next(p));
    hub.on('UsuarioDesconectado', (p: PresenciaPayload) => this.usuarioDesconectado$.next(p));
    hub.on('UsuarioEscribiendo', (p: EscribiendoPayload) => this.usuarioEscribiendo$.next(p));
    hub.on('UsuarioDejoDeEscribir', (p: EscribiendoPayload) => this.usuarioDejoDeEscribir$.next(p));
  }

  private ensureConnected(): void {
    if (!this.hub || this.hub.state !== HubConnectionState.Connected) {
      throw new Error('ChatHub no está conectado. Llamar a start() primero.');
    }
  }
}
